import {
  takeEvery,
  takeLatest,
  fork,
  call,
  put,
  select,
  all,
} from 'redux-saga/effects'
import { delay } from 'redux-saga'
import { push } from 'react-router-redux'

import store from 'store'
import * as storage from 'utils/storage'
import handleError from 'api/apiErrors'
import * as Sentry from '@sentry/react'

import * as API from 'api'
import { LOGOUT, RENDERING_DASHBOARD, RESET } from 'modules/app/constants'
import { SUBMIT_LOGIN_FORM } from 'modules/user/constants'
import { makeSelectUserID } from 'modules/user/selectors'
import { CHANGE_CURRENT_MEMBER } from 'modules/members/constants'
import {
  resetApp,
  switchMeterRequest,
  switchMeterSuccess,
  closeSwitchModal,
  getInAppMessagesSuccess,
  setHasNoMemberOrMeter,
  setImpersonatingUserId,
} from 'modules/app/actions'
import {
  loginRequest,
  loginFailure,
  loginSuccess,
  getUserRequest,
  getUserFailure,
  getUserSuccess,
  magicLinkRequested,
} from 'modules/user/actions'
import {
  getMembersRequest,
  getMembersSuccess,
  getMembersFailure,
  getEnrollmentRequest,
  getEnrollmentSuccess,
  getEnrollmentFailure,
  setCurrentMember,
  getMeterRequest,
  getMeterSuccess,
  getMeterFailure,
  getGuestsSuccess,
  createGuestSuccess,
} from 'modules/members/actions'
import { resetDeposits } from 'pages/Account/actions'
import { selectLocation } from 'modules/app/selectors'
import { makeSelectEmail } from 'modules/user/selectors'
import {
  selectFirstMember,
  selectIsGriddyGuest,
  selectEnrolledMembers,
  selectCurrentMemberID,
} from 'modules/members/selectors'
import { getCostsSaga } from 'pages/Wholesale/saga'
import { getHistorySaga } from 'pages/ElectricityUsage/saga'
import { statementSaga } from 'pages/Statement/saga'
// eslint-disable-next-line
import { getAccountSaga } from 'pages/Account/saga'
import { companyInfo, features } from 'config/profile'
import { changeLocale } from 'modules/language/actions'
import { isConsolidatedMember } from 'utils/members'
import { ENABLED_SERVICE_PROVIDERS } from 'config'

export function* getUserSaga({ userID }) {
  yield put(getUserRequest())
  try {
    const res = yield call(API.getUser, { userID })
    yield put(getUserSuccess(res))
    return res
  } catch (error) {
    yield put(getUserFailure(error.message))
    return null
  }
}

export function* getMembersSaga({ userID }) {
  yield put(getMembersRequest())
  try {
    const res = yield call(API.getMembers, { userID })
    const members = res.Members
    const enabledMembers = members.filter((m) => {
      return ENABLED_SERVICE_PROVIDERS.includes(m.service_provider)
    })

    yield put(getMembersSuccess(enabledMembers))
    return members
  } catch (error) {
    yield put(getMembersFailure(error.message))
    throw error
  }
}

export function* getAccountID({ memberID }) {
  const account = yield call(API.getAccount, { memberID })
  const accountID = account.accountID

  return accountID
}

export function* getAccountDetails({ memberID }) {
  const account = yield call(API.getAccount, { memberID })
  const balance = {
    current: account.account_balance,
    pending: account.account_pending_balance,
    estTopOffDate: account.est_top_off_date,
  }

  const details = {
    balance,
    accountNumber: account.account_number,
    billing_address: account.billing_address,
    accountID: account.accountID,
    bill_to_name: account.bill_to_name,
    autopay : account.autopay,
    creditCardFee: account.credit_card_fee,
    billing_segment: account.billing_segment,
    isPrimaryAccount: account.is_primary_account,
    primaryAccountId: account.primary_account_id,
    accountFreeze: account.account_freeze,
    isPrimaryAccount: account.is_primary_account,
    primaryAccountId: account.primary_account_id,
  }

  if (account.billing_method) details.billing_method = account.billing_method
  
  if (account.statement_delivery)
    details.statement_delivery =
      account.statement_delivery === 'email' ? true : false

  return details
}



export function* getMeterSaga({ memberID }) {
  yield put(getMeterRequest())

  try {
    const members = yield select(state => state?.members) || []
    const member = members.find(
      mem => mem.memberID === memberID
    )

    let meter = null
    if (member?.service_provider === 'cs_meter') {
      const res = yield call(API.getCommunitySolarMeter, { member_id: memberID })

      if (res.meter) {
        // Populate expected fields
        const service_address = res.meter.meta_data?.address || {}
        service_address.lineOne = service_address?.line1
        service_address.lineTwo = service_address?.line2
        meter = {
          service_address,
          meterIdentifier: res.meter.utility_account_number,
          meterID: res.meter.utility_account_number,
          status: { status: res.meter.status },
          meterIdentifierDescription: 'Account ID', // fallback in case tdsp call fails
          ...res.meter,
        }

        try {
          const tdsp = yield call(API.getTdspByDuns, { duns_number: meter.tdsp_duns })

          meter.meterIdentifierDescription = tdsp?.config?.meterIdentifier?.description || 'Account ID'
          meter.meterIdentifier2Description = tdsp?.config?.meterIdentifier2?.description
        } catch (error) {
          console.error('Error fetching TDSP data:', error)
          Sentry.captureException(error)
        }
      }
    } else if (member?.service_provider === 'agency_meter') {
      const res = yield call(API.getAgencyMeter, { member_id: memberID })
      const allowedStatuses = ['active', 'pending']
        if (res.List.length && allowedStatuses.includes(res.List[0].status)) {
          meter = res.List[0]
        }
      } else {
      const res = yield call(API.getMeter, { memberID })
      if (res.meters.length) {
        meter = res.meters[0]
      }
    }

    yield put(getMeterSuccess(meter))
    return meter
  } catch (error) {
    yield put(getMeterFailure(error.message))
    const { status } = error.response
    if (status >= 500) {
      throw error
    }
    return { error }
  }
}

export function* getCSASaga({ memberID }) {
  if (ENABLED_SERVICE_PROVIDERS.includes('csa')) {
    try {
      let csa = null
      const res = yield call(API.getCSAMeter, { member_id: memberID })
      if (res.csa) {
        csa = res.csa
      }
      return csa
    } catch (error) {
      const { status } = error.response
      if (status >= 500) {
        throw error
      }
      return { error }
    }
  }
  return null
}

export function* getEnrollmentSaga({ memberID }) {
  yield put(getEnrollmentRequest())
  try {
    const balance = yield call(getAccountDetails, { memberID })
    const meter = yield call(getMeterSaga, { memberID })
    const csa = yield call(getCSASaga, { memberID })
    yield put(getEnrollmentSuccess({ memberID, balance, meter, csa }))
    return { balance, meter, csa }
  } catch (error) {
    yield put(getEnrollmentFailure({ memberID }))
    const { status } = error.response
    if (status >= 500) {
      throw error
    }
    return { error }
  }
}

export function* getMessagesSaga({ payload: { email } }) {
  const location = yield select(selectLocation)

  if (location.pathname === '/') {
    try {
      const res = yield call(API.getInAppMessages, email)
      yield put(getInAppMessagesSuccess(res))
    } catch (error) {
      console.error(error)
      Sentry.captureException(error)
    }
  }
}

function* getMessages() {
  const email = yield select(makeSelectEmail())
  yield call(getMessagesSaga, { payload: { email } })
}

export function* login({ email, password, claims, passCode }) {
  try {
    let res = {}
    yield put(loginRequest())
    if (claims !== undefined) {
      res = {...claims}
      yield put(loginSuccess(res))
      storage.setAccessToken(res.access_token)
      storage.setRefreshToken(res.refresh_token)
    } else {      
      if (features.LOGIN_METHOD === 'MAGICLINK') {
        // we're requesting a magic link
        const source = "CUSTOMER_PORTAL"
        const type = "magic_link"
        if (passCode === undefined) {
          res = yield call(API.requestMagicLink, { email, source, type })
          if (!res.success) {
            throw new Error('Failed to request magic link')
          }
          res.magicLinkSent = true
          yield put(magicLinkRequested())
          return res
        }
        // we're verifying a magic link
        res = yield call(API.verifyMagicLink, { email, passCode, source, type })
      } else {
        // normal login with email/password}
        yield call(getFreshTurnstileToken)
        res = yield call(API.login, { email, password })        
      }
      yield put(loginSuccess(res))
      storage.setAccessToken(res.access_token)
      storage.setRefreshToken(res.refresh_token)
    }

    const user = yield call(getUserSaga, { userID: res.userID })

    // Use user langauge preference on web
    yield put(changeLocale(user.language))
    yield put(setImpersonatingUserId(res.impersonatingUserId !== undefined ? res.impersonatingUserId : null))
    
    if (user.gg_status === 'active') {
      const guests = yield call(API.getAllGuests, { userID: user.userID })
      const { Members = [] } = guests
      const guestMembers = yield all(
        Members.map(async member => {
          const { meters } = await API.getGuestMeters({
            memberID: member.memberID,
          })
          const { Members: [abandonedMember] = [] } = await API.getMembers({
            userID: user.userID,
          })
          if (abandonedMember) {
            return {
              ...member,
              meter: meters[0],
              address: abandonedMember.address,
              isGriddyGuest: true,
            }
          }
          return {
            ...member,
            meter: meters[0],
            isGriddyGuest: true,
          }
        })
      )
      yield put(getGuestsSuccess(guestMembers))
      yield put(push('/'))
      return { firstMember: guestMembers[0] }
    }

    if (user.gg_status === 'available') {
      const location = yield select(selectLocation)
      if (location.pathname.startsWith('/griddy-guest')) {
        const guest = yield call(API.createGuest, { userID: user.userID })
        const { meters } = yield call(API.getGuestMeters, {
          memberID: guest.memberID,
        })
        guest.meter = meters[0]
        const { Members: [abandonedMember] = [] } = yield call(API.getMembers, {
          userID: user.userID,
        })
        if (abandonedMember) {
          guest.address = abandonedMember.address
        }
        yield put(createGuestSuccess(guest))

        yield put(push('/'))
        return { firstMember: { ...guest, isGriddyGuest: true } }
      }
    }

    const members = yield call(getMembersSaga, { userID: res.userID })
    yield all(
      members.map(member =>
        call(getEnrollmentSaga, { memberID: member.memberID })
      )
    )

    // Consolidated accounts go first and add index for meter naming
    const updatedMembers = yield select(state => state?.members) || []

    const consolidatedMembers = updatedMembers.filter((m) => isConsolidatedMember(m)).map((m, i) => ({ ...m, nameId: i + 1 }))
    // Don't count unenrolled members in meter naming
    const nonConsolidatedMembers = updatedMembers.filter((m) => m.meter && !isConsolidatedMember(m)).map((m, i) => ({ ...m, nameId: i + 1 }))
    const rest = updatedMembers.filter((m) => !m.meter && !isConsolidatedMember(m)).map((m, i) => ({ ...m, nameId: i + 1 }))

    yield put(getMembersSuccess([...consolidatedMembers, ...nonConsolidatedMembers, ...rest]))

    const firstMember = yield select(selectFirstMember)
    return { user, firstMember }
  } catch (err) {
    yield put(loginFailure(err.message))
    // remove all query params on error
    const location = yield select(selectLocation)
    if (location.search.length > 0) {
      yield put(push(location.pathname))
    }
    return { err }
  }
}

export function* getLoginData({ payload }) {
  const { userID, memberID } = payload
  try {
    yield call(getUserSaga, { userID })
    const members = yield call(getMembersSaga, { userID })
    yield all(
      members.map(member =>
        call(getEnrollmentSaga, { memberID: member.memberID })
      )
    )

    // select current member
    for (const mem of members) {
      if (mem.memberID === memberID) {
        yield put(setCurrentMember(mem))
        break
      }
    }
  } catch (err) {
    console.log('getLoginData error', err)
  }
}

export function continueEnrollment() {
  window.open(companyInfo.signUpLink, '_self')
}

function* resetSaga() {
  const refreshToken = storage.getRefreshToken();
  // Clear token to avoid multiple logout attempts
  storage.setRefreshToken('')
  if (refreshToken) {
    try {
      yield call(API.logout, { refresh_token: refreshToken })
    } catch (error) {
      // do nothing
    }
  }
}

/**
 * handleNoMemberOrMeter indicates to the login screen that the user
 * is not activated, and resets the app in 3 seconds
 */
function* handleNoMemberOrMeter() {
  yield put(setHasNoMemberOrMeter(true))
}

export function* loginSaga({ payload, setters={setSubmitting: () => {}, setStatus: () => {}} }) {
  const { firstMember, magicLinkSent, err } = yield call(login, payload)
  if (magicLinkSent) {
    return
  }  
  if (err) {
    const { status } = err.response || { status: 500 }

    if (status >= 500) {
      const networkError = new Error(
        'Something went wrong while trying to log you in. Please try again later or call our Member Experience team if the problem persists.'
      )
      yield call(handleError, setters, networkError, 'email')
      return
    }

    yield call(handleError, setters, err, 'email')
    return
  }

  if (firstMember && firstMember.isGriddyGuest) {
    return
  }

  const allowedStateCodes = process.env.REACT_APP_ALLOWED_STATE_CODES
    ? process.env.REACT_APP_ALLOWED_STATE_CODES.split(',')
    : null
  if (
    firstMember &&
    firstMember.address &&
    allowedStateCodes &&
    !allowedStateCodes.includes(firstMember.address.state)
  ) {
    yield call(handleNoMemberOrMeter)
    return
  }

  if (firstMember && firstMember.meter) {
    yield call(setCurrentMemberSaga, { payload: { member: firstMember } })
    setters.setSubmitting(false)
    yield put(push('/'))
    return
  }
  setters.setSubmitting(false)
  yield call(handleNoMemberOrMeter)
}

export function* setCurrentMemberSaga({ payload: { member, nextMeterNum } }) {
  yield put(setCurrentMember(member))
  if (!nextMeterNum) {
    
    return
  }
  
  yield put(switchMeterRequest({ nextMeterNum }))
  yield put(resetDeposits())

  const userID = yield select(makeSelectUserID())
  const { memberID } = member
  const griddyRateClass = member.meter?.griddy_rate_class
  const location = yield select(selectLocation)

  if (location.pathname === '/') {
    const settlement_point = member.meter?.settlement_point
    if (settlement_point) {
      yield call(getCostsSaga, {
        settlementPoint: settlement_point,
      })
    }
  }
  if (location.pathname.startsWith('/usage')) {
    const ID = member.meter?.ID
    if (ID) {
      yield call(getHistorySaga, { ID })
    }
  }
  if (location.pathname.startsWith('/statement')) {
    yield call(statementSaga)
  }
  if (location.pathname.startsWith('/savings')) {
    yield call(store.dispatch.savings.getSavings, {
      memberID,
    })
  }
  if (location.pathname.startsWith('/account')) {
    yield fork(getAccountSaga, { memberID, userID })
  }

  const members = yield select(selectEnrolledMembers)
  yield put(
    switchMeterSuccess({
      meterName: `Meter ${nextMeterNum}`,
      numberOfMeters: members.length,
      griddyRateClass,
    })
  )
  yield put(setCurrentMember(member))
  yield call(delay, 1000)

  yield put(closeSwitchModal())
}

export function* logoutSaga() {
  yield put(resetApp())
  storage.remove()

  yield put(push('/login'))
}

function* updateMeter() {
  const memberID = yield select(selectCurrentMemberID)
  yield call(getEnrollmentSaga, { memberID })
}

export function* googleAnalyticsSaga(action, id) {
  let userID
  if (id) {
    userID = id
  } else {
    userID = yield select(makeSelectUserID())
  }
  try {
    while (!window.ga) {
      yield call(delay, 1000)
    }
    window.ga('set', 'dimension2', userID)
  } catch (err) {
    console.error(err)
    Sentry.captureException(err)
  }
}


export function* getFreshTurnstileToken() {
  if (process.env.REACT_APP_CF_TURNSTILE_ENABLED !== 'true') {
    throw new Error("Turnstile not enabled");
  }
  if (!process.env.REACT_APP_CF_TURNSTILE_SITE_KEY) {
    throw new Error("Turnstile not configured");
  }

  if (!window.turnstile) throw new Error("Turnstile ref not available");

  const timeoutMs = 30000;

  window.turnstile.reset();

  const token = yield call(() =>
    new Promise((resolve, reject) => {
      const start = Date.now();

      const checkToken = () => {
        const value = window.turnstile.getResponse();
        if (value) return resolve(value);
        if (Date.now() - start > timeoutMs) return reject(new Error("Timed out waiting for Turnstile token"));
        requestAnimationFrame(checkToken);
      };

      checkToken();
    })
  );

  try {
    const refresh_token = storage.getRefreshToken();
    const res = yield call(API.verifyHuman, { token, refresh_token });
    storage.setAccessToken(res.access_token);
  } catch (error) {
    console.error("Human verification failed:", error);
    Sentry.captureException(error)
    throw error;
  }
}

export default function* defaultSaga() {
  yield all([
    takeEvery(RENDERING_DASHBOARD, googleAnalyticsSaga),
    takeEvery(RENDERING_DASHBOARD, updateMeter),
    takeEvery(RENDERING_DASHBOARD, getMessages),
    takeLatest(SUBMIT_LOGIN_FORM, loginSaga),
    takeLatest(LOGOUT, logoutSaga),
    takeLatest(CHANGE_CURRENT_MEMBER, setCurrentMemberSaga),
    takeLatest(RESET, resetSaga)
  ])
}
